import { useState } from 'react';
import { Box, ChevronRight, ChevronDown, Search } from 'lucide-react';
import type { ClusterPod } from '@/types';
import { useClusterPods, useClusterNamespaces } from './useClusterData';
import PodDetailDrawer from './PodDetailDrawer';

interface Props {
  siteId: string;
}

const PHASE_OPTIONS = ['All', 'Running', 'Pending', 'Failed', 'Succeeded'] as const;

function PhaseBadge({ phase }: { phase: string }) {
  const cls = phase === 'Running' ? 'text-status-healthy bg-status-healthy/10'
    : phase === 'Pending' ? 'text-status-warning bg-status-warning/10'
    : phase === 'Failed' ? 'text-status-error bg-status-error/10'
    : phase === 'Succeeded' ? 'text-status-info bg-status-info/10'
    : 'text-silver-muted bg-surface-elevated';

  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${cls}`}>{phase}</span>;
}

function PodRow({ pod, onSelect }: { pod: ClusterPod; onSelect: (pod: ClusterPod) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`border-b border-surface-elevated hover:bg-surface-elevated/30 cursor-pointer transition-colors ${expanded ? 'bg-surface-elevated/20' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">
          <div className="flex items-center gap-1.5">
            {expanded ? <ChevronDown size={12} className="text-gold" /> : <ChevronRight size={12} className="text-silver-muted" />}
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(pod); }}
              className="hover:text-gold transition-colors text-left"
            >
              {pod.name}
            </button>
          </div>
        </td>
        <td className="px-4 py-2.5 text-xs text-silver-muted">{pod.namespace}</td>
        <td className="px-4 py-2.5"><PhaseBadge phase={pod.phase} /></td>
        <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{pod.ready}</td>
        <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{pod.nodeName}</td>
        <td className="px-4 py-2.5 text-xs text-silver-muted font-mono">{pod.restarts > 0 ? <span className={pod.restarts > 5 ? 'text-status-warning' : ''}>{pod.restarts}</span> : '0'}</td>
        <td className="px-4 py-2.5 text-xs text-silver-muted">{pod.age}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-surface-deep/50 px-6 py-3">
            <div className="space-y-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><span className="text-silver-muted">IP: </span><span className="text-silver-bright font-mono">{pod.ip || '—'}</span></div>
                <div><span className="text-silver-muted">CPU Req: </span><span className="text-silver-bright font-mono">{pod.cpuRequest || '—'}</span></div>
                <div><span className="text-silver-muted">Mem Req: </span><span className="text-silver-bright font-mono">{pod.memoryRequest || '—'}</span></div>
                <div><span className="text-silver-muted">Owner: </span><span className="text-silver-bright font-mono">{pod.ownerKind ? `${pod.ownerKind}/${pod.ownerName}` : '—'}</span></div>
              </div>
              <h4 className="text-xs font-medium text-silver-muted uppercase tracking-wider mt-2">Containers</h4>
              {pod.containers.map((c, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.state === 'running' ? 'bg-status-healthy' : c.state === 'waiting' ? 'bg-status-warning' : 'bg-status-error'}`} />
                  <span className="text-silver-bright font-mono">{c.name}</span>
                  <span className="text-silver-muted truncate max-w-xs">{c.image}</span>
                  {c.restartCount > 0 && <span className="text-status-warning">{c.restartCount} restarts</span>}
                  {c.reason && <span className="text-status-warning">({c.reason})</span>}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PodsPanel({ siteId }: Props) {
  const [namespace, setNamespace] = useState<string>('');
  const [phase, setPhase] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [selectedPod, setSelectedPod] = useState<ClusterPod | null>(null);

  const { data: namespaces } = useClusterNamespaces(siteId);
  const { data: pods, isLoading } = useClusterPods(siteId, {
    namespace: namespace || undefined,
    phase: phase !== 'All' ? phase : undefined,
    search: search || undefined,
  });

  if (isLoading && !pods) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          className="bg-surface border border-surface-elevated rounded-lg px-3 py-1.5 text-xs text-silver-bright focus:border-gold focus:outline-none"
        >
          <option value="">All namespaces</option>
          {namespaces?.map((ns) => (
            <option key={ns} value={ns}>{ns}</option>
          ))}
        </select>

        <div className="flex gap-1">
          {PHASE_OPTIONS.map((p) => (
            <button
              key={p}
              onClick={() => setPhase(p)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                phase === p
                  ? 'bg-gold/20 text-gold font-medium'
                  : 'text-silver-muted hover:text-silver-bright hover:bg-surface-elevated'
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="relative ml-auto">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-silver-muted" />
          <input
            type="text"
            placeholder="Search pods..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-surface border border-surface-elevated rounded-lg pl-7 pr-3 py-1.5 text-xs text-silver-bright placeholder:text-silver-muted/50 focus:border-gold focus:outline-none w-48"
          />
        </div>
      </div>

      {/* Pod table */}
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        {!pods || pods.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Box size={40} className="mx-auto text-silver-muted/30" />
            <p className="text-silver-muted">No pods found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-elevated">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Namespace</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Ready</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Node</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Restarts</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Age</th>
                </tr>
              </thead>
              <tbody>
                {pods.map((pod) => (
                  <PodRow key={`${pod.namespace}/${pod.name}`} pod={pod} onSelect={setSelectedPod} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pod detail drawer */}
      {selectedPod && (
        <PodDetailDrawer
          siteId={siteId}
          namespace={selectedPod.namespace}
          podName={selectedPod.name}
          onClose={() => setSelectedPod(null)}
        />
      )}
    </div>
  );
}
